import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useSyncStore } from '@/stores/syncStore';
import { db, kvGet, kvSet } from '@/lib/db';
import { supabase } from '@/lib/supabase';
import { newId } from '@/lib/ids';
import { queue } from '@/lib/sync';
import { num } from '@/lib/format';
import { QUARRY_SITE_ID } from '@/lib/fleet';
import type {
  ConsumptionFlag, ConsumptionReport, FuelDip, FuelDipChart, FuelIssue, FuelReceipt, FuelStock,
  FuelSubcontractor, FuelTank, FuelType, FuelVehicle, ReceiptBasis,
} from '@/types/models';

/** The one public-station fuel point (0042). */
export const SERVICE_STATIONS_ID = '00000000-0000-0000-0000-0000000000f1';

export const FUEL_TYPES: { value: FuelType; label: string }[] = [
  { value: 'diesel', label: 'Diesel' },
  { value: 'petrol', label: 'Petrol' },
];

export const BASIS_LABEL: Record<ReceiptBasis, string> = {
  note: 'Delivery note',
  meter: 'Meter (after − before)',
  dip: 'Dip gain (litres after − before)',
};

/** Litres, one decimal, through the app's one formatter. */
export const litres = (v: number | string | null | undefined, decimals = 1) => num(v, decimals);

/* ------------------------------------------------------------------ */
/* Who may do what — mirrors app.fuel_view / fuel_manage and the tank  */
/* policies (0042). The server enforces; this decides what is offered. */
/* ------------------------------------------------------------------ */

export interface FuelAccess {
  canUse: boolean;
  isBranch: boolean;
  /** The user's own site: their branch, or the quarry. */
  ownSite: string | null;
  /** Quarry managers and administrators: every site. */
  allSites: boolean;
  canView: (site: string | null | undefined) => boolean;
  canManage: (site: string | null | undefined) => boolean;
  canKeepTanks: (site: string | null | undefined) => boolean;
  userId: string | null;
  userName: string | null;
}

export function useFuelAccess(): FuelAccess {
  const profile = useAuthStore((s) => s.profile);
  const access = useAuthStore((s) => s.access);
  useAuthStore((s) => s.selectedBranch);   // re-render on a branch switch
  // A branch user's current branch (0055: they may have several).
  const branchId = profile?.home_site_id ? useAuthStore.getState().branchId() : null;
  const members = useAuthStore.getState().memberBranches();
  const role = profile?.is_active === false ? null : profile?.role ?? null;
  const ownSite = role ? (branchId ?? QUARRY_SITE_ID) : null;
  // Mirrors app.fuel_view / fuel_manage (0055): management at every site it
  // may see; the quarry's people at the quarry; a branch's own people there.
  const visible = (site: string | null | undefined) =>
    !!site && (!access || access.all_sites || access.sites.includes(site));
  const lead = !branchId && (role === 'manager' || role === 'admin');
  const own = (site: string | null | undefined) => !!site && (
    branchId ? site === branchId
      : (site === QUARRY_SITE_ID || members.includes(site)));
  return {
    canUse: !!ownSite && useAuthStore.getState().hasModule('fuel'),
    isBranch: !!branchId,
    ownSite,
    allSites: lead && (!access || access.all_sites),
    canView: (site) => (lead && visible(site)) || own(site),
    canManage: (site) => (lead && visible(site)) || (own(site) && (role === 'supervisor' || role === 'manager')),
    canKeepTanks: (site) => (lead && visible(site)) || (!!branchId && site === branchId && role === 'manager'),
    userId: profile?.id ?? null,
    userName: profile?.full_name ?? null,
  };
}

/* ------------------------------------------------------------------ */
/* Server lists, cached on the device                                  */
/* ------------------------------------------------------------------ */

/**
 * Every vehicle in the company, for the fuel ticket (public.fuel_vehicles).
 * A branch user cannot read the quarry's machines, but may fill them; this
 * list carries only what a ticket needs. Cached for use without signal.
 * Quarry users can fall back on their own mirror.
 */
export function useFuelVehicles(): { vehicles: FuelVehicle[] | undefined; refresh: () => Promise<void> } {
  const online = useSyncStore((s) => s.online);
  const [vehicles, setVehicles] = useState<FuelVehicle[] | undefined>(undefined);
  // The server's list wins: a slower read of the cache or the mirror must
  // not overwrite it.
  const fresh = useRef(false);

  const refresh = useCallback(async () => {
    const { data, error } = await supabase.rpc('fuel_vehicles');
    if (!error && Array.isArray(data)) {
      fresh.current = true;
      setVehicles(data as FuelVehicle[]);
      await kvSet('fuel.vehicles', data);
    }
  }, []);

  useEffect(() => {
    let live = true;
    void (async () => {
      const cached = await kvGet<FuelVehicle[]>('fuel.vehicles');
      const fallback = cached ?? await mirrorVehicles();
      if (live && !fresh.current) setVehicles(fallback);
    })();
    return () => { live = false; };
  }, []);

  useEffect(() => { if (online) void refresh(); }, [online, refresh]);
  return { vehicles, refresh };
}

async function mirrorVehicles(): Promise<FuelVehicle[]> {
  const [machines, types, sites, tanks] = await Promise.all([
    db.machines.toArray(), db.machine_types.toArray(), db.sites.toArray(), db.fuel_tanks.toArray(),
  ]);
  const type = new Map(types.map((t) => [t.id, t]));
  const site = new Map(sites.map((s) => [s.id, s.name]));
  return machines
    .filter((m) => !m.deleted_at && m.is_active && m.status !== 'disposed')
    .map((m) => ({
      id: m.id, fleet_no: m.fleet_no, name: m.name ?? null,
      type: type.get(m.machine_type_id ?? '')?.name ?? null,
      type_code: type.get(m.machine_type_id ?? '')?.code ?? null,
      description: [m.manufacturer, m.model].filter(Boolean).join(' ') || null,
      registration_no: m.registration_no ?? null,
      meter_unit: (m.hour_meter_unit ?? 'hours') as 'hours' | 'km',
      current_reading: m.current_hour_meter ?? null,
      fuel_type: m.fuel_type ?? null, fuel_tank_l: m.fuel_tank_l ?? null, fuel_norm: m.fuel_norm ?? null,
      site_id: m.site_id ?? null, site_name: site.get(m.site_id ?? '') ?? null,
      bowser_tank_id: tanks.find((t) => t.kind === 'bowser' && t.machine_id === m.id && !t.deleted_at)?.id ?? null,
      last_fill: null,
    }))
    .sort((a, b) => a.fleet_no.localeCompare(b.fleet_no));
}

/** Stock per tank (public.fuel_tank_stock), cached. */
export function useFuelStock(): { stock: FuelStock[] | undefined; refresh: () => Promise<void> } {
  const online = useSyncStore((s) => s.online);
  const [stock, setStock] = useState<FuelStock[] | undefined>(undefined);
  const fresh = useRef(false);
  const refresh = useCallback(async () => {
    const { data, error } = await supabase.rpc('fuel_tank_stock');
    if (!error && Array.isArray(data)) {
      fresh.current = true;
      setStock(data as FuelStock[]);
      await kvSet('fuel.stock', data);
    }
  }, []);
  useEffect(() => {
    let live = true;
    void kvGet<FuelStock[]>('fuel.stock').then((c) => { if (live && c && !fresh.current) setStock(c); });
    return () => { live = false; };
  }, []);
  useEffect(() => { if (online) void refresh(); }, [online, refresh]);
  return { stock, refresh };
}

/**
 * Sub-contractors' vehicles seen at the user's sites (public.fuel_subcontractors,
 * 0043), cached; without the server, the ones in this device's fills.
 */
export function useFuelSubcontractors(): { subs: FuelSubcontractor[] | undefined; refresh: () => Promise<void> } {
  const online = useSyncStore((s) => s.online);
  const [subs, setSubs] = useState<FuelSubcontractor[] | undefined>(undefined);
  const fresh = useRef(false);
  const refresh = useCallback(async () => {
    const { data, error } = await supabase.rpc('fuel_subcontractors');
    if (!error && Array.isArray(data)) {
      fresh.current = true;
      setSubs(data as FuelSubcontractor[]);
      await kvSet('fuel.subcontractors', data);
    }
  }, []);
  useEffect(() => {
    let live = true;
    void (async () => {
      const cached = await kvGet<FuelSubcontractor[]>('fuel.subcontractors');
      const fallback = cached ?? await mirrorSubcontractors();
      if (live && !fresh.current) setSubs(fallback);
    })();
    return () => { live = false; };
  }, []);
  useEffect(() => { if (online) void refresh(); }, [online, refresh]);
  return { subs, refresh };
}

async function mirrorSubcontractors(): Promise<FuelSubcontractor[]> {
  const rows = (await db.fuel_issues.toArray())
    .filter((r) => !r.deleted_at && r.subcontractor_reg)
    .sort((a, b) => b.issued_at.localeCompare(a.issued_at));
  const out = new Map<string, FuelSubcontractor>();
  for (const r of rows) {
    const reg = r.subcontractor_reg!;
    const x = out.get(reg);
    if (x) { x.fills += 1; if (!x.name && r.subcontractor_name) x.name = r.subcontractor_name; continue; }
    out.set(reg, {
      reg, name: r.subcontractor_name ?? null, unit: r.meter_unit, fills: 1,
      last_fill: { at: r.issued_at, reading: r.meter_reading, litres: r.litres, unit: r.meter_unit },
    });
  }
  return [...out.values()].sort((a, b) => a.reg.localeCompare(b.reg));
}

/** A registration as the database keys it: capitals and digits (app.fuel_reg). */
export function normReg(s: string | null | undefined): string {
  return (s ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/* ------------------------------------------------------------------ */
/* Consumption and anomalies (public.fuel_consumption, 0043)           */
/* ------------------------------------------------------------------ */

export const FLAG_LABEL: Record<ConsumptionFlag, string> = {
  first: 'First reading',
  backwards: 'Reading went backwards',
  jump: 'Reading jumped',
  no_reading: 'No reading',
  over_tank: 'More than the tank',
  high_use: 'High use',
  low_use: 'Low use',
};

/** Flags that ask for a look (First reading is information only). */
export const CHECK_FLAGS: ConsumptionFlag[] = ['backwards', 'jump', 'no_reading', 'over_tank', 'high_use', 'low_use'];

/** site: an id, null for every site the user may see, undefined while
 *  the page is still choosing (nothing is asked for yet). */
export function useFuelConsumption(from: string, to: string, site: string | null | undefined) {
  const online = useSyncStore((s) => s.online);
  const [report, setReport] = useState<ConsumptionReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!from || !to || site === undefined) return;
    const n = ++seq.current;
    setLoading(true);
    const { data, error: err } = await supabase.rpc('fuel_consumption',
      { p_from: from, p_to: to, p_site: site });
    if (n !== seq.current) return;
    setLoading(false);
    if (err) { setError(err.message); return; }
    setError(null);
    setReport(data as ConsumptionReport);
  }, [from, to, site]);
  useEffect(() => { if (online) void load(); }, [online, load]);
  return { report, error, loading, reload: load, online };
}

/** A fill by id: this device's copy, or the server's (for an older one). */
export async function findFill(id: string): Promise<FuelIssue | null> {
  const local = await db.fuel_issues.get(id);
  if (local && !local.deleted_at) return local;
  const { data } = await supabase.from('fuel_issues').select('*').eq('id', id).maybeSingle();
  return (data as FuelIssue | null) ?? null;
}

/* ------------------------------------------------------------------ */
/* Dip chart                                                           */
/* ------------------------------------------------------------------ */

/** cm → litres by straight lines between chart points, as the server
 *  does (app.fuel_dip_litres); null outside the chart. */
export function dipLitres(chart: FuelDipChart[], cm: number | null | undefined): number | null {
  if (cm == null || Number.isNaN(cm)) return null;
  const pts = chart.filter((c) => !c.deleted_at)
    .map((c) => ({ cm: Number(c.depth_cm), l: Number(c.litres) }))
    .sort((a, b) => a.cm - b.cm);
  const lo = [...pts].reverse().find((p) => p.cm <= cm);
  const hi = pts.find((p) => p.cm >= cm);
  if (!lo || !hi) return null;
  if (hi.cm === lo.cm) return lo.l;
  return Math.round((lo.l + (hi.l - lo.l) * (cm - lo.cm) / (hi.cm - lo.cm)) * 10) / 10;
}

/** Parse "cm, litres" lines (a pasted table) into chart points. */
export function parseChart(text: string): { points: { depth_cm: number; litres: number }[]; errors: string[] } {
  const points: { depth_cm: number; litres: number }[] = [];
  const errors: string[] = [];
  text.split(/\r?\n/).forEach((line, i) => {
    const t = line.trim();
    if (!t) return;
    const parts = t.split(/[\s,;\t]+/).map((x) => Number(x.replace(',', '.')));
    if (parts.length < 2 || parts.some((x) => Number.isNaN(x)) || parts[0] < 0 || parts[1] < 0) {
      errors.push(`Line ${i + 1}: "${t}" is not "cm, litres"`);
      return;
    }
    points.push({ depth_cm: parts[0], litres: parts[1] });
  });
  const seen = new Set<number>();
  for (const p of points) {
    if (seen.has(p.depth_cm)) errors.push(`${p.depth_cm} cm appears twice`);
    seen.add(p.depth_cm);
  }
  points.sort((a, b) => a.depth_cm - b.depth_cm);
  for (let i = 1; i < points.length; i++) {
    if (points[i].litres < points[i - 1].litres) {
      errors.push(`${points[i].depth_cm} cm holds less than ${points[i - 1].depth_cm} cm`);
    }
  }
  return { points, errors };
}

/* ------------------------------------------------------------------ */
/* Fills                                                               */
/* ------------------------------------------------------------------ */

export interface FillDraft {
  id: string;
  tank_id: string | null;
  /** A vehicle not in the fleet, by its registration (0043). */
  subcontractor: boolean;
  subcontractor_reg: string | null;
  subcontractor_name: string | null;
  /** A sub-contractor's reading is in km unless the ticket says hours. */
  meter_unit: 'hours' | 'km';
  machine_id: string | null;
  issued_at: string | null;
  litres: number | null;
  meter_reading: number | null;
  fuel_type: FuelType | null;
  own_use: boolean;
  attendant_name: string | null;
  slip_no: string | null;
  price_per_litre: number | null;
  notes: string | null;
}

/** What the table and its trigger refuse, caught before the outbox. */
export function validateFill(d: FillDraft, tank: FuelTank | undefined, vehicle: FuelVehicle | undefined): Record<string, string> {
  const e: Record<string, string> = {};
  if (!d.tank_id || !tank) e.tank_id = 'Choose where the fuel came from';
  if (d.subcontractor) {
    const reg = normReg(d.subcontractor_reg);
    if (!reg) e.subcontractor_reg = 'Enter the registration number';
    else if (reg.length < 2 || reg.length > 15) e.subcontractor_reg = 'Check the registration number';
  } else if (!d.machine_id || !vehicle) {
    e.machine_id = 'Choose the vehicle by its fleet number';
  }
  if (!d.issued_at) e.issued_at = 'Date and time are required';
  else if (new Date(d.issued_at).getTime() > Date.now() + 10 * 60_000) e.issued_at = 'A fill cannot be in the future';
  const l = Number(d.litres ?? 0);
  if (!(l > 0)) e.litres = 'Enter the litres';
  else if (l > 100000) e.litres = 'Too many litres for one fill';
  if (d.meter_reading == null || Number.isNaN(d.meter_reading)) {
    if (!d.subcontractor) e.meter_reading = 'Enter the reading';
  } else if (d.meter_reading < 0) e.meter_reading = 'Cannot be negative';
  if (tank?.kind === 'external') {
    if (!(d.slip_no ?? '').trim()) e.slip_no = 'Enter the slip number';
    if (!d.fuel_type) e.fuel_type = 'Choose the fuel';
    if (d.price_per_litre != null && !(d.price_per_litre > 0 && d.price_per_litre < 1000)) {
      e.price_per_litre = 'Check the price';
    }
  }
  if (!d.subcontractor && vehicle?.bowser_tank_id && vehicle.bowser_tank_id === d.tank_id && !d.own_use) {
    e.tank_id = 'A bowser cannot fill itself; tick "For the bowser\'s own engine"';
  }
  return e;
}

/** Things worth a second look, which do not stop the fill: the litres
 *  were dispensed whatever the reading says (F5). */
export function fillWarnings(
  d: FillDraft, tank: FuelTank | undefined, vehicle: FuelVehicle | undefined, sub?: FuelSubcontractor,
): string[] {
  const w: string[] = [];
  if (d.subcontractor) {
    const last = sub?.last_fill;
    if (d.meter_reading != null && last?.reading != null && last.unit === d.meter_unit && d.issued_at
        && new Date(last.at) < new Date(d.issued_at)) {
      readingWarnings(w, d, Number(last.reading), last.at, d.meter_unit);
    }
    return w;
  }
  if (!vehicle) return w;
  const last = vehicle.last_fill;
  if (d.meter_reading != null && last && d.issued_at && new Date(last.at) < new Date(d.issued_at)) {
    readingWarnings(w, d, Number(last.reading), last.at, vehicle.meter_unit);
  }
  if (vehicle.fuel_tank_l && d.litres && d.litres > Number(vehicle.fuel_tank_l) * 1.05) {
    w.push(`${litres(d.litres)} L is more than the vehicle's tank (${litres(vehicle.fuel_tank_l, 0)} L).`);
  }
  const fuel = tank?.kind === 'external' ? d.fuel_type : tank?.fuel_type;
  if (fuel && vehicle.fuel_type && fuel !== vehicle.fuel_type && !vehicle.bowser_tank_id) {
    w.push(`${vehicle.fleet_no} usually takes ${vehicle.fuel_type}, not ${fuel}.`);
  }
  return w;
}

function readingWarnings(w: string[], d: FillDraft, last: number, lastAt: string, meter: 'hours' | 'km') {
  const unit = meter === 'km' ? 'km' : 'h';
  if (d.meter_reading == null || !d.issued_at) return;
  if (d.meter_reading < last) {
    w.push(`The reading is below the last fill's (${num(last, unit === 'km' ? 0 : 1)} ${unit}). It will be kept, marked, and will not move the meter.`);
  } else {
    const days = Math.max(1, Math.ceil((new Date(d.issued_at).getTime() - new Date(lastAt).getTime()) / 86_400_000));
    const ceiling = days * (meter === 'km' ? 2000 : 24);
    if (d.meter_reading - last > ceiling) {
      w.push(`${num(d.meter_reading - last, 0)} ${unit} since the last fill ${days === 1 ? 'a day ago' : `${days} days ago`} is more than the vehicle can do. Check the reading.`);
    }
  }
}

/** A sub-contractor's fill (0043): the registration instead of a fleet number. */
export async function saveSubcontractorFill(
  d: FillDraft, tank: FuelTank, existing: FuelIssue | undefined, userId: string | null, ownSite: string | null,
): Promise<FuelIssue> {
  const external = tank.kind === 'external';
  const row: FuelIssue = {
    ...(existing ?? {}),
    id: d.id,
    tank_id: tank.id,
    machine_id: null,
    subcontractor_reg: normReg(d.subcontractor_reg),
    subcontractor_name: d.subcontractor_name?.trim() || null,
    issued_at: d.issued_at!,
    litres: Number(d.litres),
    fuel_type: (external ? d.fuel_type : tank.fuel_type) ?? 'diesel',
    meter_reading: d.meter_reading == null || Number.isNaN(d.meter_reading) ? null : Number(d.meter_reading),
    meter_unit: d.meter_unit,
    reading_check: existing?.reading_check ?? 'ok',
    site_id: tank.kind === 'site' ? tank.site_id! : (existing?.site_id ?? ownSite ?? tank.site_id ?? QUARRY_SITE_ID),
    tank_site_id: tank.site_id,
    vehicle_site_id: null,
    to_tank_id: null,
    to_site_id: null,
    own_use: false,
    attendant_name: d.attendant_name?.trim() || null,
    operator_code: existing?.operator_code ?? null,
    slip_no: external ? (d.slip_no?.trim() || null) : null,
    price_per_litre: external ? d.price_per_litre : null,
    notes: d.notes?.trim() || null,
    source: existing?.source ?? 'app',
    external_ref: existing?.external_ref ?? null,
    created_by: existing?.created_by ?? userId,
    created_at: existing?.created_at ?? new Date().toISOString(),
    updated_at: new Date().toISOString(),
    updated_by: userId,
    deleted_at: null,
  } as FuelIssue;
  await db.fuel_issues.put(row);
  const payload: Record<string, unknown> = {
    id: row.id, tank_id: row.tank_id, machine_id: null, subcontractor_reg: row.subcontractor_reg,
    subcontractor_name: row.subcontractor_name, issued_at: row.issued_at, litres: row.litres,
    meter_reading: row.meter_reading, meter_unit: row.meter_unit, own_use: false,
    attendant_name: row.attendant_name, slip_no: row.slip_no, price_per_litre: row.price_per_litre,
    notes: row.notes,
  };
  if (external) payload.fuel_type = row.fuel_type;
  await queue({ entity: 'fuel_issue', entity_id: row.id, op: 'upsert', payload });
  return row;
}

export async function saveFill(
  d: FillDraft, tank: FuelTank, vehicle: FuelVehicle, existing: FuelIssue | undefined, userId: string | null,
): Promise<FuelIssue> {
  const external = tank.kind === 'external';
  const transfer = !!vehicle.bowser_tank_id && !d.own_use;
  const row: FuelIssue = {
    ...(existing ?? {}),
    id: d.id,
    tank_id: tank.id,
    machine_id: vehicle.id,
    subcontractor_reg: null,
    subcontractor_name: null,
    issued_at: d.issued_at!,
    litres: Number(d.litres),
    fuel_type: (external ? d.fuel_type : tank.fuel_type) ?? 'diesel',
    meter_reading: Number(d.meter_reading),
    meter_unit: vehicle.meter_unit,
    reading_check: existing?.reading_check ?? 'ok',
    // What the server will work out, so the lists are right offline.
    site_id: tank.kind === 'site' ? tank.site_id! : (vehicle.site_id ?? tank.site_id ?? QUARRY_SITE_ID),
    tank_site_id: tank.site_id,
    vehicle_site_id: vehicle.site_id,
    to_tank_id: transfer ? vehicle.bowser_tank_id : null,
    to_site_id: transfer ? vehicle.site_id : null,
    own_use: !!vehicle.bowser_tank_id && d.own_use,
    attendant_name: d.attendant_name?.trim() || null,
    operator_code: existing?.operator_code ?? null,
    slip_no: external ? (d.slip_no?.trim() || null) : null,
    price_per_litre: external ? d.price_per_litre : null,
    notes: d.notes?.trim() || null,
    source: existing?.source ?? 'app',
    external_ref: existing?.external_ref ?? null,
    created_by: existing?.created_by ?? userId,
    created_at: existing?.created_at ?? new Date().toISOString(),
    updated_at: new Date().toISOString(),
    updated_by: userId,
    deleted_at: null,
  } as FuelIssue;
  await db.fuel_issues.put(row);
  // The columns a person decides; the trigger works out the rest.
  const payload: Record<string, unknown> = {
    id: row.id, tank_id: row.tank_id, machine_id: row.machine_id, subcontractor_reg: null,
    subcontractor_name: null, issued_at: row.issued_at,
    litres: row.litres, meter_reading: row.meter_reading, own_use: row.own_use,
    attendant_name: row.attendant_name, slip_no: row.slip_no, price_per_litre: row.price_per_litre,
    notes: row.notes,
  };
  if (external) payload.fuel_type = row.fuel_type;
  await queue({ entity: 'fuel_issue', entity_id: row.id, op: 'upsert', payload });
  return row;
}

export async function removeFill(id: string): Promise<void> {
  await db.fuel_issues.delete(id);
  await queue({ entity: 'fuel_issue', entity_id: id, op: 'delete', payload: null });
}

/* ------------------------------------------------------------------ */
/* Deliveries                                                          */
/* ------------------------------------------------------------------ */

export interface ReceiptDraft {
  id: string;
  tank_id: string | null;
  received_at: string | null;
  delivery_note_no: string | null;
  supplier_name: string | null;
  driver_name: string | null;
  delivery_vehicle_reg: string | null;
  litres_delivered: number | null;
  meter_before: number | null;
  meter_after: number | null;
  dip_before_cm: number | null;
  dip_after_cm: number | null;
  litres_before: number | null;
  litres_after: number | null;
  price_per_litre: number | null;
  notes: string | null;
}

export interface ReceiptFigures {
  metered: number | null;
  before: number | null;
  after: number | null;
  gain: number | null;
  /** Tank gain against the delivery note. */
  variance: number | null;
  variancePct: number | null;
  /** Meter against the delivery note. */
  meterVariance: number | null;
  booked: number | null;
  bookedOn: ReceiptBasis;
}

/** The delivery's own arithmetic, as the trigger books it (0042). */
export function receiptFigures(d: ReceiptDraft, tank: FuelTank | undefined, chart: FuelDipChart[]): ReceiptFigures {
  const chartOn = tank?.dip_mode === 'chart';
  const before = d.litres_before ?? (chartOn ? dipLitres(chart, d.dip_before_cm) : null);
  const after = d.litres_after ?? (chartOn ? dipLitres(chart, d.dip_after_cm) : null);
  const metered = d.meter_before != null && d.meter_after != null ? d.meter_after - d.meter_before : null;
  const gain = before != null && after != null ? after - before : null;
  const note = d.litres_delivered;
  const variance = gain != null && note ? gain - note : null;
  let booked: number | null = note;
  let bookedOn: ReceiptBasis = 'note';
  if (tank?.receipt_basis === 'meter' && metered != null && metered > 0) { booked = metered; bookedOn = 'meter'; }
  else if (tank?.receipt_basis === 'dip' && gain != null && gain > 0) { booked = gain; bookedOn = 'dip'; }
  return {
    metered, before, after, gain, variance,
    variancePct: variance != null && note ? (variance / note) * 100 : null,
    meterVariance: metered != null && note ? metered - note : null,
    booked, bookedOn,
  };
}

export function validateReceipt(d: ReceiptDraft): Record<string, string> {
  const e: Record<string, string> = {};
  if (!d.tank_id) e.tank_id = 'Choose the tank';
  if (!d.received_at) e.received_at = 'Date and time are required';
  else if (new Date(d.received_at).getTime() > Date.now() + 10 * 60_000) e.received_at = 'A delivery cannot be in the future';
  if (!(Number(d.litres_delivered ?? 0) > 0)) e.litres_delivered = 'Enter the litres on the delivery note';
  if (d.meter_before != null && d.meter_after != null && d.meter_after < d.meter_before) {
    e.meter_after = 'The meter after must not be below the meter before';
  }
  if (d.price_per_litre != null && !(d.price_per_litre > 0 && d.price_per_litre < 1000)) e.price_per_litre = 'Check the price';
  return e;
}

export async function saveReceipt(
  d: ReceiptDraft, tank: FuelTank, chart: FuelDipChart[], existing: FuelReceipt | undefined, userId: string | null,
): Promise<FuelReceipt> {
  const f = receiptFigures(d, tank, chart);
  const clean = (v: string | null) => v?.trim() || null;
  const row: FuelReceipt = {
    ...(existing ?? {}),
    id: d.id, tank_id: tank.id, site_id: tank.site_id ?? QUARRY_SITE_ID,
    received_at: d.received_at!,
    delivery_note_no: clean(d.delivery_note_no), supplier_name: clean(d.supplier_name),
    driver_name: clean(d.driver_name), delivery_vehicle_reg: clean(d.delivery_vehicle_reg)?.toUpperCase() ?? null,
    litres_delivered: Number(d.litres_delivered),
    meter_before: d.meter_before, meter_after: d.meter_after,
    dip_before_cm: d.dip_before_cm, dip_after_cm: d.dip_after_cm,
    litres_before: d.litres_before ?? (tank.dip_mode === 'chart' ? f.before : null),
    litres_after: d.litres_after ?? (tank.dip_mode === 'chart' ? f.after : null),
    metered_litres: f.metered, tank_gain: f.gain,
    litres_booked: f.booked ?? Number(d.litres_delivered), booked_on: f.bookedOn,
    price_per_litre: d.price_per_litre, notes: clean(d.notes),
    created_by: existing?.created_by ?? userId,
    created_at: existing?.created_at ?? new Date().toISOString(),
    updated_at: new Date().toISOString(), updated_by: userId, deleted_at: null,
  } as FuelReceipt;
  await db.fuel_receipts.put(row);
  const payload: Record<string, unknown> = {
    id: row.id, tank_id: row.tank_id, received_at: row.received_at,
    delivery_note_no: row.delivery_note_no, supplier_name: row.supplier_name, driver_name: row.driver_name,
    delivery_vehicle_reg: row.delivery_vehicle_reg, litres_delivered: row.litres_delivered,
    meter_before: row.meter_before, meter_after: row.meter_after,
    dip_before_cm: row.dip_before_cm, dip_after_cm: row.dip_after_cm,
    // Typed litres only; the server reads the chart itself.
    litres_before: d.litres_before, litres_after: d.litres_after,
    price_per_litre: row.price_per_litre, notes: row.notes,
  };
  await queue({ entity: 'fuel_receipt', entity_id: row.id, op: 'upsert', payload });
  return row;
}

export async function removeReceipt(id: string): Promise<void> {
  await db.fuel_receipts.delete(id);
  await queue({ entity: 'fuel_receipt', entity_id: id, op: 'delete', payload: null });
}

/* ------------------------------------------------------------------ */
/* Dips                                                                */
/* ------------------------------------------------------------------ */

export interface DipDraft {
  id: string;
  tank_id: string | null;
  dipped_at: string | null;
  dip_cm: number | null;
  litres: number | null;
  notes: string | null;
}

export function validateDip(d: DipDraft, tank: FuelTank | undefined, chart: FuelDipChart[]): Record<string, string> {
  const e: Record<string, string> = {};
  if (!d.tank_id || !tank) e.tank_id = 'Choose the tank';
  if (!d.dipped_at) e.dipped_at = 'Date and time are required';
  else if (new Date(d.dipped_at).getTime() > Date.now() + 10 * 60_000) e.dipped_at = 'A dip cannot be in the future';
  const fromChart = tank?.dip_mode === 'chart' ? dipLitres(chart, d.dip_cm) : null;
  if (d.litres == null && fromChart == null) {
    e.litres = tank?.dip_mode === 'chart' && d.dip_cm != null
      ? 'The dip chart does not cover this depth: type the litres'
      : 'Enter the litres';
  } else if ((d.litres ?? 0) < 0) e.litres = 'Cannot be negative';
  return e;
}

export async function saveDip(
  d: DipDraft, tank: FuelTank, chart: FuelDipChart[], existing: FuelDip | undefined, userId: string | null,
): Promise<FuelDip> {
  const l = d.litres ?? (tank.dip_mode === 'chart' ? dipLitres(chart, d.dip_cm) : null);
  const row: FuelDip = {
    ...(existing ?? {}),
    id: d.id, tank_id: tank.id, site_id: tank.site_id ?? QUARRY_SITE_ID,
    dipped_at: d.dipped_at!, dip_cm: d.dip_cm, litres: Number(l), notes: d.notes?.trim() || null,
    created_by: existing?.created_by ?? userId,
    created_at: existing?.created_at ?? new Date().toISOString(),
    updated_at: new Date().toISOString(), updated_by: userId, deleted_at: null,
  } as FuelDip;
  await db.fuel_dips.put(row);
  await queue({
    entity: 'fuel_dip', entity_id: row.id, op: 'upsert',
    payload: { id: row.id, tank_id: row.tank_id, dipped_at: row.dipped_at, dip_cm: row.dip_cm,
               litres: d.litres, notes: row.notes },
  });
  return row;
}

export async function removeDip(id: string): Promise<void> {
  await db.fuel_dips.delete(id);
  await queue({ entity: 'fuel_dip', entity_id: id, op: 'delete', payload: null });
}

export function blankFill(tankId: string | null, attendant: string | null): FillDraft {
  return {
    id: newId(), tank_id: tankId, subcontractor: false, subcontractor_reg: null, subcontractor_name: null,
    meter_unit: 'km', machine_id: null, issued_at: new Date().toISOString(),
    litres: null, meter_reading: null, fuel_type: null, own_use: false,
    attendant_name: attendant, slip_no: null, price_per_litre: null, notes: null,
  };
}

/** A fill's place in words: the tank, or "Service Stations". */
export function pointName(tanks: Map<string, FuelTank>, id: string | null | undefined): string {
  if (!id) return '—';
  return tanks.get(id)?.name ?? (id === SERVICE_STATIONS_ID ? 'Service Stations' : '—');
}
